import { useState, useEffect, useRef } from "react";
import * as pdfjs from "pdfjs-dist";
import { Loader2 } from "lucide-react";

interface MobilePDFPreviewProps {
  pdfBlob: Blob;
  className?: string;
}

/**
 * Mobile-optimized PDF preview component
 * Renders all pages as canvas elements in a vertical scrollable layout
 * Optimized for touch devices, especially iPad Safari
 */
export function MobilePDFPreview({
  pdfBlob,
  className = "",
}: MobilePDFPreviewProps) {
  const [pdf, setPdf] = useState<any>(null);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set());

  // Load PDF from blob
  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        const arrayBuffer = await pdfBlob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const loadingTask = pdfjs.getDocument(uint8Array);
        const pdfDoc = await loadingTask.promise;

        if (!cancelled) {
          setPdf(pdfDoc);
          setNumPages(pdfDoc.numPages);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load PDF for mobile preview:", err);
          setError("Failed to load PDF");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfBlob]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center text-gray-600">
          <p className="mb-2">Failed to load PDF preview</p>
          <p className="text-sm">Please use the download button below</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`overflow-auto bg-gray-100 ${className}`}>
      <div className="flex flex-col gap-4 p-4">
        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
          <PDFPageCanvas
            key={pageNum}
            pdf={pdf}
            pageNumber={pageNum}
            onLoaded={() => {
              setLoadedPages((prev) => new Set(prev).add(pageNum));
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Individual PDF page renderer using canvas
 */
interface PDFPageCanvasProps {
  pdf: any;
  pageNumber: number;
  onLoaded?: () => void;
}

function PDFPageCanvas({ pdf, pageNumber, onLoaded }: PDFPageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [rendered, setRendered] = useState(false);
  const renderingRef = useRef(false);
  const renderTaskRef = useRef<any>(null);

  useEffect(() => {
    if (!pdf || rendered || renderingRef.current) return;

    renderingRef.current = true;

    const renderPage = async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Calculate scale based on container width
        const containerWidth = containerRef.current?.clientWidth || 300;
        const viewport = page.getViewport({ scale: 1.0 });

        // Scale to fit container width (minus padding)
        const scale = (containerWidth - 32) / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        // Set canvas dimensions
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const context = canvas.getContext("2d");
        if (!context) return;

        // Cancel any previous render task on this canvas
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        // Render PDF page to canvas
        const task = page.render({
          canvasContext: context,
          viewport: scaledViewport,
        });
        renderTaskRef.current = task;
        await task.promise;

        setRendered(true);
        onLoaded?.();
      } catch (err: any) {
        // Ignore cancellation errors
        if (err?.name === "RenderingCancelledException") return;
        console.error(`Failed to render page ${pageNumber}:`, err);
      } finally {
        renderingRef.current = false;
      }
    };

    renderPage();

    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      renderingRef.current = false;
    };
  }, [pdf, pageNumber, rendered, onLoaded]);

  return (
    <div
      ref={containerRef}
      className="bg-white shadow-md rounded-lg overflow-hidden"
    >
      {!rendered && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="w-full h-auto"
        style={{ display: rendered ? "block" : "none" }}
      />
      {rendered && (
        <div className="text-center text-sm text-gray-500 py-2 border-t">
          Page {pageNumber}
        </div>
      )}
    </div>
  );
}
